import { z } from 'zod';
import { coordSchema, endReasonSchema, fleetSchema, gameStatusSchema, PROTOCOL_VERSION, rulesetSchema, seatSchema, shipSchema, shotResultSchema, shotSchema, } from './primitives.js';
/**
 * Identyfikator komendy generowany przez klienta.
 *
 * Serwer pamięta ostatnio obsłużone identyfikatory w pokoju, więc ponowienie komendy
 * po zerwaniu połączenia NIE powoduje drugiego strzału — zwracany jest wynik pierwszego.
 * To jest cała odpowiedź na zawodną sieć mobilną.
 */
export const commandIdSchema = z.string().min(8).max(64);
/* ── Komendy: klient → serwer ─────────────────────────────────────────────── */
export const commandSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('room.join'), code: z.string().length(6) }),
    z.object({ type: z.literal('room.leave') }),
    z.object({ type: z.literal('game.placeFleet'), fleet: fleetSchema }),
    z.object({ type: z.literal('game.ready') }),
    z.object({ type: z.literal('game.fire'), x: coordSchema.shape.x, y: coordSchema.shape.y }),
    z.object({ type: z.literal('game.resign') }),
    /** Rezygnacja w trakcie rozstawiania — partia bez zwycięzcy, poza statystykami. */
    z.object({ type: z.literal('game.cancel') }),
    /**
     * Podłączenie do partii, którą gracz już zajmuje. `lastSeq: 0` to pierwsze wejście,
     * wartość większa od zera to wznowienie po zerwaniu połączenia.
     */
    z.object({
        type: z.literal('session.resume'),
        gameId: z.uuid(),
        lastSeq: z.number().int().min(0),
    }),
]);
/* ── Zdarzenia: serwer → klient ───────────────────────────────────────────── */
/** Widok partii oczami jednego gracza. Flota przeciwnika tylko po zakończeniu partii. */
export const playerViewSchema = z.object({
    gameId: z.uuid(),
    status: gameStatusSchema,
    ruleset: rulesetSchema,
    you: seatSchema,
    turn: seatSchema.nullable(),
    own: z.object({
        fleet: fleetSchema.nullable(),
        ready: z.boolean(),
        incoming: z.array(shotSchema),
    }),
    enemy: z.object({
        ready: z.boolean(),
        shots: z.array(shotSchema),
        sunkShips: z.array(z.object({
            id: shipSchema.shape.id,
            size: shipSchema.shape.size,
            cells: z.array(coordSchema),
        })),
        fleet: fleetSchema.nullable(),
    }),
    winner: seatSchema.nullable(),
    endReason: endReasonSchema.nullable(),
});
export const eventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('room.state'),
        /** Kod zaproszenia; `null`, gdy pokój jest już pełny. */
        code: z.string().length(6).nullable(),
        players: z.array(z.object({
            seat: seatSchema,
            nickname: z.string(),
            online: z.boolean(),
            /** Czy gracz zatwierdził flotę. Sama flota pozostaje tajna. */
            ready: z.boolean(),
        })),
    }),
    z.object({ type: z.literal('game.snapshot'), view: playerViewSchema }),
    z.object({ type: z.literal('game.started'), firstSeat: seatSchema }),
    z.object({
        type: z.literal('game.turn'),
        seat: seatSchema,
        /** Moment upływu czasu na ruch. `null` w partii bez limitu. */
        deadlineMs: z.number().int().nullable(),
    }),
    z.object({
        type: z.literal('game.shot'),
        seat: seatSchema,
        x: coordSchema.shape.x,
        y: coordSchema.shape.y,
        result: shotResultSchema,
        /** Pola odkryte automatycznie po zatopieniu (aureola). */
        revealed: z.array(coordSchema),
        sunkShip: z.object({ id: shipSchema.shape.id, size: shipSchema.shape.size }).nullable(),
        automatic: z.boolean(),
    }),
    /**
     * Obecność gracza przy stole. Zdarzenie jest symetryczne — niesie numer miejsca,
     * a nie „przeciwnika" — więc ten sam ładunek trafia do obu stron.
     */
    z.object({
        type: z.literal('game.presence'),
        seat: seatSchema,
        online: z.boolean(),
        /** Ile czasu zostało do uznania partii za porzuconą; `null`, gdy gracz jest obecny. */
        graceMs: z.number().int().nullable(),
    }),
    z.object({ type: z.literal('game.clock'), remainingMs: z.number().int().nullable() }),
    z.object({
        type: z.literal('game.over'),
        winner: seatSchema.nullable(),
        reason: endReasonSchema,
        /**
         * Po zakończeniu partii obie floty są jawne — dopiero tu wolno je pokazać.
         * Jeden ładunek dla obu graczy: zakończona partia nie ma już czego ukrywać.
         */
        fleets: z.tuple([fleetSchema, fleetSchema]).nullable(),
    }),
]);
/* ── Koperta ──────────────────────────────────────────────────────────────── */
export const errorCodeSchema = z.enum([
    'BAD_REQUEST',
    'UNAUTHORIZED',
    'ROOM_NOT_FOUND',
    'ROOM_FULL',
    'WRONG_STATUS',
    'NOT_YOUR_TURN',
    'ALREADY_SHOT',
    'OUT_OF_BOUNDS',
    'ILLEGAL_FLEET',
    'FLEET_NOT_SET',
    'ALREADY_READY',
    'TIMEOUTS_DISABLED',
    'RATE_LIMITED',
    'PROTOCOL_VERSION_MISMATCH',
    'INTERNAL',
]);
const versionSchema = z.literal(PROTOCOL_VERSION);
export const clientEnvelopeSchema = z.object({
    v: versionSchema,
    kind: z.literal('cmd'),
    id: commandIdSchema,
    cmd: commandSchema,
});
export const serverEnvelopeSchema = z.discriminatedUnion('kind', [
    z.object({
        v: versionSchema,
        kind: z.literal('evt'),
        // Numeracja zaczyna się od zera: snapshot wysłany przed jakimkolwiek zdarzeniem
        // znaczy „nie widziałeś jeszcze niczego" i od tej wartości liczy się wznowienie.
        seq: z.number().int().min(0),
        evt: eventSchema,
    }),
    z.object({ v: versionSchema, kind: z.literal('ack'), id: commandIdSchema }),
    z.object({
        v: versionSchema,
        kind: z.literal('err'),
        id: commandIdSchema.optional(),
        code: errorCodeSchema,
        message: z.string(),
    }),
]);
/** Maksymalny rozmiar ramki WebSocket przyjmowanej od klienta (ochrona przed ramką-bombą). */
export const MAX_FRAME_BYTES = 16 * 1024;
//# sourceMappingURL=messages.js.map